import { AnalyticsEventParams } from './model-analytics'

interface UmamiPayload {
  website: string
  hostname: string
  screen: string
  language: string
  title: string
  url: string
  referrer: string
  tag?: string
  id?: string
  name?: string
  data?: AnalyticsEventParams
}

interface UmamiTracker {
  track: (payloadModifier: (payload: UmamiPayload) => UmamiPayload) => void
  identify: (uniqueIdOrData: string | AnalyticsEventParams, data?: AnalyticsEventParams) => void
}

declare global {
  interface Window {
    umami?: UmamiTracker
  }
}

export interface UmamiConfig {
  host: string
  websiteId: string
}

const SCRIPT_ID = 'umami-analytics'
const DISTINCT_ID_MAX_LENGTH = 50

let userId: string | undefined
let userProperties: AnalyticsEventParams | undefined

function identifyUser() {
  if (!window.umami || (!userId && !userProperties)) return

  if (userId) {
    if (userProperties) window.umami.identify(userId, userProperties)
    else window.umami.identify(userId)
    return
  }

  window.umami.identify(userProperties!)
}

function handleLoad(onLoad: () => void) {
  identifyUser()
  onLoad()
}

export function init(config: UmamiConfig, onLoad: () => void) {
  if (!config.host || !config.websiteId || document.getElementById(SCRIPT_ID)) return

  const script = document.createElement('script')
  script.id = SCRIPT_ID
  script.async = true
  script.defer = true
  script.src = `${config.host}/script.js`
  script.onload = () => handleLoad(onLoad)
  script.setAttribute('data-website-id', config.websiteId)
  script.setAttribute('data-auto-track', 'false')

  document.head.appendChild(script)
}

export function remove() {
  document.getElementById(SCRIPT_ID)?.remove()
  delete window.umami
  userId = undefined
  userProperties = undefined
}

export function logEvent(eventName: string, eventParams: AnalyticsEventParams | undefined, rota: string) {
  window.umami?.track((payload) => ({ ...payload, name: eventName, data: eventParams, url: rota }))
}

export function logPageview(rota: string) {
  window.umami?.track((payload) => ({ ...payload, url: rota }))
}

export function setUserId(id: string) {
  const newUserId = id.slice(0, DISTINCT_ID_MAX_LENGTH)
  if (userId && userId !== newUserId) userProperties = undefined

  userId = newUserId
  identifyUser()
}

export function setUserProperties(properties: AnalyticsEventParams) {
  userProperties = properties
  identifyUser()
}
